import { ListChecks } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import type { GeometryTotals } from '@obraplan/core/domain/takeoff/geometry';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/feedback/states';
import { KpiStrip } from '@/features/reports/KpiStrip';
import type { KpiItem } from '@/features/reports/kpis';
import { AptosDaTipologia } from './AptosDaTipologia';
import { Consolidacao } from './Consolidacao';
import type { RecorteDaTipologia } from './escopo-da-tipologia';
import { TiposDaObra } from './EstacasNaPrancha';
import { ItensPorServico } from './ItensPorServico';
import type { FuncoesDaObra } from './do-servidor';
import { medidosDaTipologia, totaisDaTipologia } from './medido-da-tipologia';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  resumoDosItens,
  type ResumoDosItens,
} from './resumo-dos-itens';
import { useServicosAprovados } from './servicos-aprovados';
import type { TypologyDraft, WorkspaceAction, WorkspaceDraft } from './typology-draft';
import { useCustoDaMaoDeObra } from './use-custo-da-mao-de-obra';
import { usePrecosDaObra } from './use-precos-da-obra';

/**
 * A metade do quantitativo que trata de **material**, e não de geometria.
 *
 * Era uma rota irmã e virou aba: montar a tipologia e lançar o que ela consome são o mesmo
 * trabalho, e trocar de página no meio dele era o atrito.
 *
 * Tudo o que esta aba mostra é o **recorte da tipologia**: os serviços escolhidos para ela,
 * das disciplinas que os andares dela têm na Estrutura, com o filtro do topo por cima. A
 * faixa de números, a lista, os aptos e a consolidação recebem o mesmo recorte e por isso
 * concordam — antes cada um lia o catálogo inteiro da obra, e a faixa dizia "150 serviços"
 * sobre uma tipologia que usa doze.
 *
 * Qual tipologia está aberta fica na faixa do topo da página, junto das abas e do filtro:
 * é a mesma pergunta nas duas abas, e respondê-la em dois lugares era o que confundia.
 *
 * Os preços de material da obra (Fase 29, etapa 2) são lidos aqui, uma vez, e entregues às
 * duas tabelas: a de itens os mostra em cada linha, a consolidação os decide. Lidos em cada
 * uma, a gravação de um preço numa não apareceria na outra. O custo da mão de obra (etapa 3)
 * segue o mesmo caminho: o R$/h de cada função é lido aqui e mostrado nas duas.
 *
 * Recebe o rascunho por parâmetro de propósito. Chamar `useWorkspaceDraft` aqui daria uma
 * **segunda cópia viva** do mesmo rascunho enquanto a montagem tem a dela, e as duas
 * gravariam por cima uma da outra no `localStorage` — a última a salvar apagaria a outra,
 * em silêncio. Com o estado no topo da página, isso não acontece.
 */
export function ItensEConsolidacao({
  workspace,
  draft,
  tipologia,
  dispatch,
  recorte,
  recorteDe,
  filtrado,
  onLimparFiltro,
  onEscolherServicos,
  onAbrirEstrutura,
  funcoes,
  mediaDoApartamento,
}: {
  /** As funções que a mão de obra de cada serviço pode apontar (RN-252). */
  funcoes?: FuncoesDaObra;
  /** A média do apartamento da obra (Fase 40): ao lado do índice, e a faixa "a média mudou". */
  mediaDoApartamento?: ApartmentAverageForProject;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  /** O que o desenho determina para esta tipologia — a base de cada lançamento. */
  tipologia: GeometryTotals;
  dispatch: React.Dispatch<WorkspaceAction>;
  recorte: RecorteDaTipologia;
  /** O recorte de cada tipologia, para a soma da obra inteira. */
  recorteDe: (typology: TypologyDraft) => ServiceCatalog;
  filtrado: boolean;
  onLimparFiltro: () => void;
  onEscolherServicos: () => void;
  onAbrirEstrutura: () => void;
}) {
  const catalog = recorte.catalogo;
  /**
   * O medido de cada serviço — a base do índice de consumo (Fase 24, fatia 6).
   *
   * O da mesa sai dos totais que a página já calculou; o das outras tipologias, da mesma
   * conta feita para cada uma (`medidosDaTipologia`), que é o que a soma da obra precisa.
   */
  // O bloco, o baldrame e o radier com fator ou taxa de aço (Fase 41, RN-539, RN-545): só
  // quando alguma peça tem.
  const fundacao = useMemo(() => fundacaoDaTipologia(workspace, draft), [workspace, draft]);
  const medidos = useMemo(
    () => medidosDaTipologia(catalog, tipologia, draft, fundacao),
    [catalog, tipologia, draft, fundacao],
  );
  const medidosDe = useCallback(
    (typology: TypologyDraft): Readonly<Record<string, number>> =>
      typology.unitTypeId === draft.unitTypeId
        ? medidos
        : medidosDaTipologia(
            workspace.catalog ?? catalog,
            totaisDaTipologia(workspace, typology),
            typology,
            fundacaoDaTipologia(workspace, typology),
          ),
    [workspace, catalog, draft.unitTypeId, medidos],
  );
  // Os cômodos com fator próprio (Fase 39, RN-512): só refaz o desenho quando algum tem.
  const comodos = useMemo(() => comodosDaTipologia(workspace, draft), [workspace, draft]);
  // O índice da estaca é por tipo (Fase 39, RN-508): a tabela diz o nome de cada um.
  const nomesDosTipos = useMemo(
    () => new Map((workspace.pileTypes ?? []).map((tipo) => [tipo.id as string, tipo.name])),
    [workspace.pileTypes],
  );
  const resumo = useMemo(
    () =>
      resumoDosItens(
        catalog,
        draft,
        medidos,
        pileDaTipologia(draft, workspace.pileTypes),
        comodos,
        fundacao,
      ),
    [catalog, draft, medidos, workspace.pileTypes, comodos, fundacao],
  );
  const precos = usePrecosDaObra();
  const custo = useCustoDaMaoDeObra();
  // Os serviços aprovados no orçamento (Fase 33, RN-438): o cadeado, e nada para editar neles.
  const aprovados = useServicosAprovados();
  const escolhidos = recorte.escolha.chaves.size;
  const semDisciplina = recorte.disciplinasDoAndar.size === 0 && recorte.foraDoAndar.size === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-raised) px-4 py-3">
        <ListChecks className="size-5 shrink-0 text-(--color-content-muted)" aria-hidden="true" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium">
            {escolhidos === 0
              ? `Nenhum serviço escolhido em ${draft.name}`
              : `${String(escolhidos)} ${escolhidos === 1 ? 'serviço escolhido' : 'serviços escolhidos'} em ${draft.name}`}
          </span>
          <span className="text-xs text-(--color-content-muted)">
            {recorte.escolha.gravada
              ? `De ${String(recorte.disciplinasDoAndar.size)} ${recorte.disciplinasDoAndar.size === 1 ? 'disciplina' : 'disciplinas'} dos andares desta tipologia.`
              : escolhidos === 0
                ? 'Escolha os serviços que esta tipologia usa.'
                : 'Ponto de partida: os que já têm quantidade. Abra o painel e salve para fixar.'}
          </span>
        </div>
        {!recorte.escolha.gravada && escolhidos > 0 && <Badge tone="info">não gravada</Badge>}
        <Button variant="primary" onClick={onEscolherServicos}>
          Escolher serviços
        </Button>
      </div>

      {semDisciplina ? (
        <Card>
          <CardContent className="pt-5">
            <EmptyState
              title="Os andares desta tipologia não têm disciplina"
              description={`A lista de serviços sai das disciplinas que a Estrutura cadastrou nos pavimentos onde ${draft.name} acontece. Cadastre-as na Estrutura — ou ligue o pavimento ao desenho, na montagem — e os serviços delas aparecem aqui.`}
              action={{ label: 'Abrir a Estrutura', onClick: onAbrirEstrutura }}
            />
          </CardContent>
        </Card>
      ) : escolhidos === 0 ? (
        <Card>
          <CardContent className="pt-5">
            <EmptyState
              title="Nenhum serviço escolhido"
              description="O catálogo da obra é uma biblioteca: escolha nele o que esta tipologia usa. Só o escolhido aparece para lançar e entra na soma."
              action={{ label: 'Escolher serviços', onClick: onEscolherServicos }}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <KpiStrip columns={4} items={faixaDeResumo(resumo)} />

          <ItensPorServico
            catalog={catalog}
            catalogoDaObra={workspace.catalog ?? catalog}
            draft={draft}
            tipologia={tipologia}
            medidos={medidos}
            {...(comodos === undefined ? {} : { comodos })}
            {...(fundacao === undefined ? {} : { fundacao })}
            dispatch={dispatch}
            foraDoAndar={recorte.foraDoAndar}
            filtrado={filtrado}
            onLimparFiltro={onLimparFiltro}
            onAbrirEstrutura={onAbrirEstrutura}
            {...(funcoes === undefined ? {} : { funcoes })}
            precos={precos}
            custo={custo}
            {...(aprovados === undefined ? {} : { aprovados })}
            nomesDosTipos={nomesDosTipos}
            {...(mediaDoApartamento === undefined ? {} : { mediaDoApartamento })}
            // O fator de cada tipo muda nos Tipos de estaca — o mesmo botão da planta.
            tiposDeEstaca={
              <TiposDaObra workspace={workspace} projectId={undefined} dispatch={dispatch} />
            }
          />

          <AptosDaTipologia
            catalog={catalog}
            workspace={workspace}
            draft={draft}
            dispatch={dispatch}
            {...(aprovados === undefined ? {} : { aprovados })}
          />

          <Consolidacao
            catalog={catalog}
            workspace={workspace}
            draft={draft}
            recorteDe={recorteDe}
            medidosDe={medidosDe}
            precos={precos}
            custo={custo}
          />
        </>
      )}
    </div>
  );
}

/** `1 item` · `3 itens`. */
function contar(count: number, singular: string, many: string): string {
  return `${String(count)} ${count === 1 ? singular : many}`;
}

/**
 * Os quatro números do topo, já escritos.
 *
 * Cor só onde há o que fazer: item sem quantidade e material sem código de compras são
 * pendências — o primeiro deixa a soma incompleta, o segundo não vira pedido. O resto é
 * contagem, e contagem em verde é o mesmo que nenhuma cor.
 */
function faixaDeResumo(resumo: ResumoDosItens): readonly KpiItem[] {
  const semQuantidade = resumo.itens - resumo.lancados;
  const semCodigo = resumo.materiais - resumo.comCodigo;

  return [
    {
      label: 'Serviços com material',
      value: String(resumo.comMaterial),
      hint:
        resumo.servicos === 0
          ? 'nenhum serviço no recorte'
          : resumo.comMaterial === resumo.servicos
            ? `todos os ${contar(resumo.servicos, 'serviço', 'serviços')} escolhidos`
            : `de ${String(resumo.servicos)} escolhidos — o resto ainda não tem item`,
      tone: resumo.comMaterial < resumo.servicos ? 'warn' : 'default',
    },
    {
      label: 'Itens lançados',
      value: String(resumo.lancados),
      hint:
        resumo.itens === 0
          ? 'nenhum item nos serviços'
          : semQuantidade === 0
            ? `todos os ${contar(resumo.itens, 'item', 'itens')} têm quantidade`
            : `de ${String(resumo.itens)} · ${contar(semQuantidade, 'sem quantidade', 'sem quantidade')}`,
      tone: semQuantidade > 0 ? 'warn' : 'default',
    },
    {
      label: 'Materiais a comprar',
      value: String(resumo.materiais),
      hint:
        resumo.materiais === 0
          ? 'nada com quantidade ainda'
          : semCodigo === 0
            ? 'todos com código de compras'
            : `${contar(semCodigo, 'sem código', 'sem código')} de compras`,
      tone: semCodigo > 0 ? 'warn' : 'default',
    },
    {
      label: 'Mão de obra',
      value: `${String(Math.round(resumo.horas))} HH`,
      hint:
        resumo.funcoes === 0
          ? 'nenhuma função lançada'
          : `${contar(resumo.funcoes, 'função', 'funções')} · fora do pedido de compra`,
    },
  ];
}
